import { useCallback, useEffect, useState } from 'react';
import { leadgenApi, type LeadGenCampaign } from '@/api/leadgen.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { showApiError } from '@/utils/apiError';
import { usePermissions } from '@/hooks/usePermissions';
import { notify } from '@/core/toast/notify';
export function LeadGenCampaignsPage() {
  const { hasAny } = usePermissions();
  const canManage = hasAny('LEAD_GEN_CAMPAIGN', 'LEAD_GEN_MANAGE');
  const [campaigns, setCampaigns] = useState<LeadGenCampaign[]>([]);
  const [name, setName] = useState('');
  const [budget, setBudget] = useState('10000');
  const [cost, setCost] = useState('8500');
  const [channel, setChannel] = useState('Google Ads');
  const [revenueAttribution, setRevenueAttribution] = useState('32000');
  const [attributionModel, setAttributionModel] = useState<'first' | 'last' | 'multi'>('multi');

  const load = useCallback(async () => {
    try {
      setCampaigns(await leadgenApi.listCampaigns());
    } catch (err) {
      showApiError(err, 'Failed to load campaigns');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) return;
    try {
      await leadgenApi.createCampaign({
        name,
        budget: Number(budget),
        cost: Number(cost),
        channel,
        revenueAttribution: Number(revenueAttribution),
        active: true
      });
      notify.success('Marketing campaign registered');
      setName('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create campaign');
    }
  };

  const calculateRoi = (c: LeadGenCampaign) => {
    if (c.cost <= 0) return '0%';
    const roi = ((c.revenueAttribution - c.cost) / c.cost) * 100;
    return `${roi.toFixed(1)}%`;
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Marketing Campaigns & ROI"
        description="Link advertising costs, UTM parameters, and multi-touch revenue attribution modeling."
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Lead Gen', to: '/lead-gen' },
          { label: 'Campaigns' },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {canManage && (
        <div className="lg:col-span-2 card p-5 space-y-4">
          <h3 className="section-title">Register Acquisition Campaign</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Campaign Name" placeholder="e.g. Q2 Enterprise Cloud Promo" value={name} onChange={(e) => setName(e.target.value)} />
            <Input label="Ad Platform / Channel" placeholder="e.g. LinkedIn Ads" value={channel} onChange={(e) => setChannel(e.target.value)} />
            <Input label="Allocated Budget (INR)" type="number" value={budget} onChange={(e) => setBudget(e.target.value)} />
            <Input label="Actual Cost (INR)" type="number" value={cost} onChange={(e) => setCost(e.target.value)} />
            <Input label="Revenue Attributed (INR)" type="number" value={revenueAttribution} onChange={(e) => setRevenueAttribution(e.target.value)} />
            <Select
              label="Attribution Model"
              value={attributionModel}
              onChange={(v) => setAttributionModel(v as typeof attributionModel)}
              options={[
                { value: 'first', label: 'First Touch (Full credit to initial click)' },
                { value: 'last', label: 'Last Touch (Full credit to conversion page)' },
                { value: 'multi', label: 'Multi-Touch (Weighted split across touchpoints)' },
              ]}
            />
          </div>
          <Button className="w-full" onClick={create}>Save Campaign</Button>
        </div>
        )}

        <div className={`card p-5 space-y-4 ${canManage ? '' : 'lg:col-span-3'}`}>
          <h3 className="section-title">Attributions overview</h3>
          <ul className="space-y-4">
            {campaigns.map(c => (
              <li key={c.id} className="border border-slate-100 rounded-lg p-4 bg-slate-50 space-y-2">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-bold text-slate-800">{c.name}</p>
                    <p className="text-xs text-slate-400">Channel: {c.channel}</p>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">ROI: {calculateRoi(c)}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center pt-2">
                  <div className="rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-800">
                    <p className="text-[10px] text-slate-400">Budget</p>
                    <p className="font-bold text-slate-700 text-xs">₹{c.budget}</p>
                  </div>
                  <div className="rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-800">
                    <p className="text-[10px] text-slate-400">Cost</p>
                    <p className="font-bold text-slate-700 text-xs">₹{c.cost}</p>
                  </div>
                  <div className="rounded border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-800">
                    <p className="text-[10px] text-slate-400">Revenue</p>
                    <p className="font-bold text-slate-700 text-xs text-primary-600">₹{c.revenueAttribution}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
